import { ALL_SPELLS, type SpellId } from '../game/types';
import {
  ATTACK_TYPES,
  CLIP_ACTIONS,
  COMBAT_AI_PROPERTIES,
  DAMAGE_TYPES,
  VOWELS,
  type AttackType,
  type CharacterDef,
  type Roster,
  type Vowel,
} from './types';

const MOTION_STYLES: readonly string[] = ['walk', 'jump', 'fly', 'float', 'blink'];
/** Sensible bounds for the voice tuning: outside them the synthesis stops sounding like a voice. */
const VOICE_RANGES: Record<string, [number, number]> = {
  pitch: [40, 1200],
  formantScale: [0.5, 2],
  growl: [0, 1],
  breathiness: [0, 1],
  tempo: [0.25, 4],
};

export class RosterError extends Error {
  constructor(message: string) {
    super(`Invalid roster: ${message}`);
    this.name = 'RosterError';
  }
}

/** Validates untrusted JSON (bundled or user content) into a roster keyed by character id. */
export function validateRoster(input: unknown): Roster {
  if (!Array.isArray(input)) throw new RosterError('expected an array of characters');
  const roster: Record<string, CharacterDef> = {};
  for (const raw of input) {
    const c = validateCharacter(raw);
    if (roster[c.id]) throw new RosterError(`duplicate character id '${c.id}'`);
    roster[c.id] = c;
  }
  return Object.freeze(roster);
}

function validateCharacter(raw: unknown): CharacterDef {
  if (typeof raw !== 'object' || raw === null) throw new RosterError('character must be an object');
  const c = raw as Record<string, unknown>;
  if (typeof c.id !== 'string' || !/^[a-z0-9][a-z0-9-]{0,63}$/.test(c.id))
    throw new RosterError(`character id ${JSON.stringify(c.id)} must be kebab-case`);
  const where = `character '${c.id}'`;
  if (typeof c.name !== 'string' || !c.name.trim())
    throw new RosterError(`${where}: name is required`);
  if (!Number.isInteger(c.tier) || (c.tier as number) < 1 || (c.tier as number) > 5)
    throw new RosterError(`${where}: tier must be an integer 1–5`);
  const movement = c.movement as Record<string, unknown> | undefined;
  if (
    !movement ||
    !['ground', 'fly', 'teleport'].includes(movement.type as string) ||
    !Number.isInteger(movement.range) ||
    (movement.range as number) < 1 ||
    (movement.range as number) > 12
  )
    throw new RosterError(`${where}: movement needs a type (ground|fly|teleport) and a range 1–12`);
  if (typeof c.hitPoints !== 'number' || c.hitPoints <= 0)
    throw new RosterError(`${where}: hitPoints must be positive`);
  const attack = c.attack as Record<string, unknown> | undefined;
  if (
    !attack ||
    !ATTACK_TYPES.includes(attack.type as AttackType) ||
    typeof attack.damage !== 'number' ||
    attack.damage < 0
  )
    throw new RosterError(`${where}: attack needs a type and a non-negative damage`);
  // The bounds a game's own characters are held to, so a bundled roster is held to them too.
  if (
    attack.interval !== undefined &&
    (typeof attack.interval !== 'number' || !(attack.interval >= 0.1 && attack.interval <= 60))
  )
    throw new RosterError(
      `${where}: attack interval ${JSON.stringify(attack.interval)} must be 0.1–60 seconds`,
    );
  if (attack.damageType !== undefined && !DAMAGE_TYPES.includes(attack.damageType as never))
    throw new RosterError(`${where}: attack damageType must be one of ${DAMAGE_TYPES.join(', ')}`);
  if (
    attack.effectChance !== undefined &&
    (typeof attack.effectChance !== 'number' || attack.effectChance < 0 || attack.effectChance > 1)
  )
    throw new RosterError(`${where}: attack effectChance must be between 0 and 1`);
  if (
    attack.reach !== undefined &&
    (attack.type !== 'burn' ||
      typeof attack.reach !== 'number' ||
      attack.reach < 0.5 ||
      attack.reach > 5)
  )
    throw new RosterError(`${where}: attack reach must be 0.5–5 and is only valid for burn`);
  if (
    attack.duration !== undefined &&
    (attack.type !== 'burn' ||
      typeof attack.duration !== 'number' ||
      attack.duration < 1 ||
      attack.duration > 10)
  )
    throw new RosterError(
      `${where}: attack duration must be 1–10 seconds and is only valid for burn`,
    );
  if (c.combat !== undefined) validateCombat(c.combat, where);
  if (c.force !== undefined && (!Number.isInteger(c.force) || (c.force as number) <= 0))
    throw new RosterError(`${where}: force must be a positive integer`);
  if (c.classicEquivalent !== undefined && typeof c.classicEquivalent !== 'string')
    throw new RosterError(`${where}: classicEquivalent must be a string`);
  if (c.spellcaster !== undefined && typeof c.spellcaster !== 'boolean')
    throw new RosterError(`${where}: spellcaster must be a boolean`);
  // Checked for a game's characters only, before (core-rules:quality#10): a typo in a bundled
  // roster lost the piece its magic, or gave it points it could never spend, without a word.
  if (
    c.spellPoints !== undefined &&
    (!Number.isInteger(c.spellPoints) ||
      (c.spellPoints as number) < 0 ||
      (c.spellPoints as number) > 1000)
  )
    throw new RosterError(
      `${where}: spellPoints ${JSON.stringify(c.spellPoints)} must be a whole number 0–1000`,
    );
  if (c.spells !== undefined) {
    if (!Array.isArray(c.spells)) throw new RosterError(`${where}: spells must be a list`);
    const unknown = c.spells.find((s) => !ALL_SPELLS.includes(s as SpellId));
    if (unknown !== undefined)
      throw new RosterError(`${where}: unknown spell ${JSON.stringify(unknown)}`);
  }
  if (c.summoned !== undefined && typeof c.summoned !== 'boolean')
    throw new RosterError(`${where}: summoned must be a boolean`);
  if (c.model !== undefined) {
    const model = c.model as Record<string, unknown>;
    if (
      typeof model !== 'object' ||
      model === null ||
      typeof model.file !== 'string' ||
      !model.file.endsWith('.glb')
    )
      throw new RosterError(`${where}: model.file must be a .glb path`);
    if (
      model.boardFile !== undefined &&
      (typeof model.boardFile !== 'string' || !model.boardFile.endsWith('.glb'))
    )
      throw new RosterError(`${where}: model.boardFile must be a .glb path`);
    if (
      model.boardHeight !== undefined &&
      (typeof model.boardHeight !== 'number' || model.boardHeight <= 0)
    )
      throw new RosterError(`${where}: model.boardHeight must be positive`);
    if (model.yaw !== undefined && typeof model.yaw !== 'number')
      throw new RosterError(`${where}: model.yaw must be a number`);
    if (
      model.motion !== undefined &&
      (typeof model.motion !== 'string' || !MOTION_STYLES.includes(model.motion))
    )
      throw new RosterError(`${where}: model.motion must be one of ${MOTION_STYLES.join(', ')}`);
    if (
      model.clips !== undefined &&
      (typeof model.clips !== 'object' ||
        model.clips === null ||
        Object.values(model.clips).some((v) => typeof v !== 'string'))
    )
      throw new RosterError(`${where}: model.clips must map actions to clip names`);
    // A misspelt action ('atack') was never played: the procedural motion stood in, unremarked.
    const action = Object.keys((model.clips ?? {}) as object).find(
      (key) => !CLIP_ACTIONS.includes(key as never),
    );
    if (action !== undefined)
      throw new RosterError(
        `${where}: model.clips names '${action}', which is not one of ${CLIP_ACTIONS.join(', ')}`,
      );
  }
  if (c.voice !== undefined) {
    const voice = c.voice as Record<string, unknown>;
    if (typeof voice !== 'object' || voice === null)
      throw new RosterError(`${where}: voice must be an object`);
    for (const [key, [min, max]] of Object.entries(VOICE_RANGES)) {
      const value = voice[key];
      if (value !== undefined && (typeof value !== 'number' || !(value >= min && value <= max)))
        throw new RosterError(`${where}: voice.${key} must be a number ${min}–${max}`);
    }
    if (
      voice.vowels !== undefined &&
      (!Array.isArray(voice.vowels) ||
        voice.vowels.length === 0 ||
        voice.vowels.some((v) => !VOWELS.includes(v as Vowel)))
    )
      throw new RosterError(`${where}: voice.vowels must list one or more of ${VOWELS.join(' ')}`);
  }
  return raw as CharacterDef;
}

function validateCombat(raw: unknown, where: string): void {
  if (typeof raw !== 'object' || raw === null)
    throw new RosterError(`${where}: combat must be an object`);
  const combat = raw as Record<string, unknown>;
  if (
    combat.speed !== undefined &&
    (typeof combat.speed !== 'number' || combat.speed < 0.4 || combat.speed > 2)
  )
    throw new RosterError(`${where}: combat.speed must be between 0.4 and 2`);
  if (
    combat.protection !== undefined &&
    (typeof combat.protection !== 'number' || combat.protection < 0 || combat.protection > 0.75)
  )
    throw new RosterError(`${where}: combat.protection must be between 0 and 0.75`);
  if (combat.resistances !== undefined) {
    if (typeof combat.resistances !== 'object' || combat.resistances === null)
      throw new RosterError(`${where}: combat.resistances must be an object`);
    for (const [type, amount] of Object.entries(combat.resistances)) {
      if (!DAMAGE_TYPES.includes(type as never))
        throw new RosterError(`${where}: unknown resistance '${type}'`);
      if (typeof amount !== 'number' || amount < 0 || amount > 0.95)
        throw new RosterError(`${where}: resistance '${type}' must be between 0 and 0.95`);
    }
  }
  if (combat.aiLevels !== undefined) {
    if (!combat.aiLevels || typeof combat.aiLevels !== 'object' || Array.isArray(combat.aiLevels))
      throw new RosterError(`${where}: aiLevels must be an object`);
    for (const [level, factors] of Object.entries(combat.aiLevels)) {
      if (
        !['novice', 'fair', 'master'].includes(level) ||
        !factors ||
        typeof factors !== 'object' ||
        Array.isArray(factors)
      )
        throw new RosterError(`${where}: invalid AI level '${level}'`);
      for (const [key, value] of Object.entries(factors)) {
        if (
          !COMBAT_AI_PROPERTIES.includes(key as never) ||
          typeof value !== 'number' ||
          !Number.isFinite(value) ||
          value < 0 ||
          value > 8
        )
          throw new RosterError(`${where}: invalid AI factor '${key}'`);
      }
    }
  }
  if (combat.levels !== undefined) {
    if (typeof combat.levels !== 'object' || combat.levels === null)
      throw new RosterError(`${where}: combat.levels must be an object`);
    for (const [level, rawAdjustment] of Object.entries(combat.levels)) {
      if (!['novice', 'fair', 'master'].includes(level))
        throw new RosterError(`${where}: unknown combat level '${level}'`);
      if (typeof rawAdjustment !== 'object' || rawAdjustment === null)
        throw new RosterError(`${where}: combat level '${level}' must be an object`);
      for (const [property, value] of Object.entries(rawAdjustment)) {
        if (
          !['hitPoints', 'damage', 'attackInterval', 'effectChance', 'protection'].includes(
            property,
          )
        )
          throw new RosterError(`${where}: unknown combat level property '${property}'`);
        if (typeof value !== 'number' || value < 0.25 || value > 4)
          throw new RosterError(
            `${where}: combat level '${level}.${property}' must be between 0.25 and 4`,
          );
      }
    }
  }
}
